import React, { createContext, useContext, useState, useEffect } from 'react';

const UserRightsContext = createContext();

// Use same backend URL as other APIs (gateway on 3002) so JWT auth and CORS apply
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/$/, '');
const ROLES_API_BASE = BACKEND_URL + '/roles';
const CURRENT_USER_ID_URL = BACKEND_URL + '/synctrack/current-user-id'; // current user id for non-CDX (separate from user-lookup)

/** Renamed functions: new architecture code first, then legacy rights keys from the roles DB */
const FUNCTION_CODE_LEGACY_ALIASES = {
  'Data Schema': ['TF Tenant Mappings'],
  'Global Data Schema': ['TF Global Mappings'],
  Transformation: ['Data Schema', 'TF Tenant Mappings'],
  'Global Transformation': ['Global Data Schema', 'TF Global Mappings'],
  // Menu architecture renames (display/functionCode → old functions.name)
  'Design Share': ['File Share'],
  'Chat Hub': ['Sync Hub'],
  'Agent Tools': ['Tenant Tools'],
  'Model Configuration': ['Model configuration'],
  Connector: ['Connector Token'],
  Integration: ['My Integrations'],
  Webhook: ['Webhooks'],
  Roles: ['Tenant Roles'],
  Users: ['Tenant Users'],
  Tenant: ['Tenants'],
  'Benchmark Hub': ['Benchmark Dashboard'],
  Projects: ['Benchmark Projects'],
  'Cost Structure': ['Code Standard'],
  'Cost Benchmark': ['Benchmark'],
};

/** Screen functionCode (label) → master_menu.menu_code — stable across display renames */
const FUNCTION_CODE_TO_MENU_CODE = {
  'Sync Flow': 'HUB_SYNC_FLOW',
  'Sync Flows': 'HUB_SYNC_FLOW',
  'Hub Flow': 'HUB_HUB_FLOW',
  'Hub Flows': 'HUB_HUB_FLOW',
  Transformation: 'HUB_TRANSFORMATION',
  'Data Schema': 'HUB_DATA_SCHEMA',
  'Data Point': 'HUB_DATA_POINT',
  'Design Share': 'SF_DESIGN_SHARE',
  'Knowledge Hub': 'SF_KNOWLEDGE_HUB',
  'File Exchange': 'SF_FILE_EXCHANGE',
  'Benchmark Hub': 'SF_BENCHMARK_HUB',
  Projects: 'SF_BENCHMARK_PROJECTS',
  'Benchmark Projects': 'SF_BENCHMARK_PROJECTS',
  'Cost Structure': 'SF_CODE_STANDARD',
  'Code Standard': 'SF_CODE_STANDARD',
  'Cost Benchmark': 'SF_BENCHMARK',
  Benchmark: 'SF_BENCHMARK',
  'Project Comparison': 'SF_BENCHMARK_COMPARISON',
  'Bid Comparison': 'SF_BENCHMARK_BID_COMPARISON',
  'Agent Setup': 'AI_AGENT_SETUP',
  'Agent Tools': 'AI_AGENT_TOOLS',
  'Model Configuration': 'AI_MODEL_CONFIG',
  'Task Queue': 'AI_TASK_QUEUE',
  'Chat Hub': 'AI_CHAT_HUB',
  'Agent Teams': 'AI_AGENT_TEAMS',
  'PDF Hub': 'AI_PDF_HUB',
  'Execution Logs': 'AI_EXECUTION_LOGS',
  'DB Config': 'CFG_DB_CONFIG',
  Connector: 'CFG_CONNECTOR',
  Roles: 'CFG_ROLES',
  Users: 'CFG_USERS',
  Integration: 'CFG_INTEGRATION',
  Webhook: 'CFG_WEBHOOK',
  'App Config': 'CFG_APP_CONFIG',
  Notifications: 'CFG_NOTIFICATIONS',
  'Audit Logs': 'CFG_AUDIT_LOGS',
  Dashboard: 'CDX_DASHBOARD',
  Tenant: 'CDX_TENANT',
  'Test API': 'CDX_TEST_API',
  'Master Connector': 'CDX_MASTER_CONNECTOR',
  'Master Flows': 'CDX_MASTER_FLOWS',
  'Global Transformation': 'CDX_GLOBAL_TRANSFORM',
  'Global Data Schema': 'CDX_GLOBAL_DATA_SCHEMA',
  'Global Tools': 'CDX_GLOBAL_TOOLS',
  'Master Menus': 'CDX_MASTER_MENUS',
  'Global Users': 'CDX_GLOBAL_USERS',
  License: 'CDX_LICENSE',
  'AI Global Schema': 'CDX_AI_GLOBAL_SCHEMA',
  'File Share Usage': 'CDX_FILE_SHARE_USAGE',
  'Webhook Event Registry': 'CDX_WEBHOOK_EVENT_REG',
  'Global Link Relationships': 'CDX_GLOBAL_LINK_REL',
  Help: 'HELP_HELP',
};

const normalizeRights = (right) => ({
  access: Boolean(right.access),
  add: Boolean(right.add),
  edit: Boolean(right.edit),
  view: Boolean(right.view),
  execute: Boolean(right.execute),
});

const indexRight = (rightsMap, right) => {
  const fr = normalizeRights(right);
  const menuCode = (right.menu_code || right.menuCode || '').toString().trim().toUpperCase();
  const name = right.function_name || right.functionName || '';
  const code = right.function_code || right.functionCode || '';
  if (menuCode) rightsMap[menuCode] = fr;
  if (name) rightsMap[name] = fr;
  if (code && code !== menuCode && code !== name) rightsMap[code] = fr;
};

const resolveFunctionRights = (userRights, functionCode) => {
  if (!functionCode) return null;
  const primary = userRights[functionCode];
  if (primary) return primary;
  const menuCode = FUNCTION_CODE_TO_MENU_CODE[functionCode];
  if (menuCode && userRights[menuCode]) return userRights[menuCode];
  const upper = String(functionCode).toUpperCase();
  if (userRights[upper]) return userRights[upper];
  const aliases = FUNCTION_CODE_LEGACY_ALIASES[functionCode];
  if (!aliases) return null;
  for (const alt of aliases) {
    const fr = userRights[alt] || (FUNCTION_CODE_TO_MENU_CODE[alt] && userRights[FUNCTION_CODE_TO_MENU_CODE[alt]]);
    if (fr) return fr;
  }
  return null;
};

export const useUserRights = () => {
  const context = useContext(UserRightsContext);
  if (!context) {
    throw new Error('useUserRights must be used within a UserRightsProvider');
  }
  return context;
};

export const UserRightsProvider = ({ children }) => {
  const [userRights, setUserRights] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rightsLoaded, setRightsLoaded] = useState(false);

  const isHarmonateUser = typeof window !== 'undefined' && localStorage.getItem('harmonate_user') === 'true';
  const isCdxUser = typeof window !== 'undefined' && localStorage.getItem('cdx_user') === 'true';
  const hasFullAccess = isHarmonateUser || isCdxUser; // roles/rights not applicable
  const userId = typeof window !== 'undefined'
    ? (localStorage.getItem('userId') || localStorage.getItem('user_id'))
    : null;

  const fetchUserRights = async (targetUserId = null) => {
    if (hasFullAccess) {
      setUserRights({});
      setRightsLoaded(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const tenantIdForHeader = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      if (tenantIdForHeader) headers['X-tenant-id'] = tenantIdForHeader;

      // For non-CDX: get current user id from /me, then call user-rights API (separate from user-lookup)
      let effectiveUserId = targetUserId || userId;
      if (!effectiveUserId) {
        const meRes = await fetch(CURRENT_USER_ID_URL, { method: 'GET', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
        if (!meRes.ok) {
          setUserRights({});
          setRightsLoaded(true);
          return;
        }
        const meData = await meRes.json();
        effectiveUserId = meData?.userId != null ? meData.userId : (meData?.user_id != null ? meData.user_id : null);
        if (effectiveUserId != null) {
          try { localStorage.setItem('userId', String(effectiveUserId)); } catch { /* ignore */ }
        }
      }
      if (!effectiveUserId) {
        setUserRights({});
        setRightsLoaded(true);
        return;
      }

      const response = await fetch(`${ROLES_API_BASE}/user-rights/${effectiveUserId}`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) {
        setUserRights({});
        setRightsLoaded(true);
        return;
      }
      const data = await response.json();
      const currentTenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
      const rightsMap = {};
      if (data?.tenant_rights && Array.isArray(data.tenant_rights)) {
        const currentTenantRights = data.tenant_rights.find(
          (t) => String(t.tenant_id) === String(currentTenantId)
        );
        if (currentTenantRights?.user_rights) {
          currentTenantRights.user_rights.forEach((right) => indexRight(rightsMap, right));
        } else if (Array.isArray(data.user_rights)) {
          // tenant_rights present but no match for current tenant — use top-level list
          data.user_rights.forEach((right) => indexRight(rightsMap, right));
        } else if (data.tenant_rights.length === 1 && Array.isArray(data.tenant_rights[0]?.user_rights)) {
          data.tenant_rights[0].user_rights.forEach((right) => indexRight(rightsMap, right));
        }
      } else if (data?.user_rights && Array.isArray(data.user_rights)) {
        data.user_rights.forEach((right) => indexRight(rightsMap, right));
      } else if (Array.isArray(data)) {
        data.forEach((right) => indexRight(rightsMap, right));
      }
      setUserRights(rightsMap);
      setRightsLoaded(true);
    } catch (err) {
      console.error('Error fetching user rights:', err);
      setError(err.message);
      setUserRights({});
      setRightsLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (functionCode, action) => {
    if (hasFullAccess) return true;
    if (!rightsLoaded) return false;
    const fr = resolveFunctionRights(userRights, functionCode);
    if (!fr) return false;
    return Boolean(fr[action]);
  };

  const hasAnyPermission = (permissions) => {
    if (hasFullAccess) return true;
    if (!Array.isArray(permissions)) return false;
    return permissions.some((p) => hasPermission(p.functionCode, p.action));
  };

  const isFunctionEnabled = (functionCode) => {
    if (hasFullAccess) return true;
    const fr = resolveFunctionRights(userRights, functionCode);
    return fr ? Boolean(fr.access) : false;
  };

  const canAccessMenu = (functionCode) => {
    if (hasFullAccess) return true;
    if (!rightsLoaded) return false;
    const fr = resolveFunctionRights(userRights, functionCode);
    return fr ? Boolean(fr.access) : false;
  };

  const canAccessFunction = (functionCode) => {
    if (hasFullAccess) return true;
    if (!rightsLoaded) return false;
    const fr = resolveFunctionRights(userRights, functionCode);
    return fr ? Boolean(fr.access || fr.view) : false;
  };

  const canAdd = (functionCode) => hasPermission(functionCode, 'add');
  const canEdit = (functionCode) => hasPermission(functionCode, 'edit');
  const canView = (functionCode) => hasPermission(functionCode, 'view') || canAccessMenu(functionCode);
  const canExecute = (functionCode) => hasPermission(functionCode, 'execute');

  const getFunctionPermissions = (functionCode) => {
    if (hasFullAccess) {
      return { access: true, add: true, edit: true, view: true, execute: true };
    }
    return (
      resolveFunctionRights(userRights, functionCode) || {
        access: false,
        add: false,
        edit: false,
        view: false,
        execute: false,
      }
    );
  };

  const refreshUserRights = () => {
    setRightsLoaded(false);
    fetchUserRights();
  };

  const resetUserRights = () => {
    setUserRights({});
    setRightsLoaded(false);
    setLoading(false);
    setError(null);
  };

  useEffect(() => {
    // CDX / full access: no rights API, but mark loaded so UI buttons are not stuck disabled
    if (hasFullAccess) {
      if (!rightsLoaded) {
        setUserRights({});
        setRightsLoaded(true);
        setLoading(false);
      }
      return;
    }
    if (!rightsLoaded) fetchUserRights();
  }, [rightsLoaded, hasFullAccess]);

  useEffect(() => {
    const handleLogout = () => resetUserRights();
    window.addEventListener('logout-success', handleLogout);
    return () => window.removeEventListener('logout-success', handleLogout);
  }, []);

  const value = {
    userRights,
    loading,
    error,
    rightsLoaded,
    hasPermission,
    hasAnyPermission,
    isFunctionEnabled,
    getFunctionPermissions,
    canAccessMenu,
    canAccessFunction,
    canAdd,
    canEdit,
    canView,
    canExecute,
    refreshUserRights,
    fetchUserRights,
    resetUserRights,
    isHarmonateUser,
    isCdxUser,
    hasFullAccess,
    ROLES_API_BASE,
    getRolesHeaders: () => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const tenantId = typeof window !== 'undefined' ? (localStorage.getItem('tenantId') || localStorage.getItem('tenant_id')) : null;
      const h = { 'Content-Type': 'application/json' };
      if (token) h.Authorization = `Bearer ${token}`;
      if (tenantId) h['X-tenant-id'] = tenantId;
      return h;
    },
  };

  return (
    <UserRightsContext.Provider value={value}>
      {children}
    </UserRightsContext.Provider>
  );
};
